import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { wgs84FromLambert72, isInBelgium, type GeoPoint } from '@uniticket/shared';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Google Maps links (keyless — the official Maps URLs API, no billing, no script) ───────────────
//
// Ces liens s'ancrent sur les COORDONNÉES du DMP quand on les a, jamais sur le texte de l'adresse
// (demande user, réunion 2026-08-27 : « comment est-ce que tu définis le point ? »). Le texte oblige
// Google à re-géocoder pour son propre compte, donc à trancher lui-même sur l'adresse — précisément
// ce qui est en litige dans le ticket qu'on est en train de valider, et souvent introuvable chez lui
// quand l'adresse vient d'être créée. Le x/y Belmap stocké est la position qui fait foi de notre côté ;
// le point se calcule depuis lui, voir `addressPoint`.
//
// 6 décimales = ~11 cm : au-delà on transporte du bruit de conversion, pas de la précision.
function coord(lat: number, lon: number): string {
  return `${lat.toFixed(6)},${lon.toFixed(6)}`;
}

/** Le point à montrer pour une adresse : Street View intégré et liens Google (2026-10-16). Il part des x/y
 *  Lambert 72, convertis ici au mètre près, et non du lat/lon STOCKÉ : celui-là est faux de ~836 m sur toutes les
 *  adresses que l'app a créées (son convertisseur omet thêta et le datum, packages/api/src/lib/geo.ts), alors que
 *  leurs x/y sont justes. Le lat/lon stocké ne sert que quand x/y manquent. Rien de plausible en Belgique → null,
 *  et le bouton se grise. */
export function addressPoint(
  ref: { x?: number | null; y?: number | null; lat?: number | null; lon?: number | null } | null | undefined,
): GeoPoint | null {
  if (!ref) return null;
  if (ref.x != null && ref.y != null) {
    const p = wgs84FromLambert72(Number(ref.x), Number(ref.y));
    if (isInBelgium(p)) return p;
  }
  if (ref.lat != null && ref.lon != null) {
    const p = { lat: Number(ref.lat), lon: Number(ref.lon) };
    if (isInBelgium(p)) return p;
  }
  return null;
}

/** Street View ouvert directement sur le point (Google accroche le panorama le plus proche). */
export function streetViewUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coord(lat, lon)}`;
}

/** Carte avec une épingle EXACTEMENT sur le point — c'est elle qu'on mesure jusqu'à la rue. */
export function mapPinUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${coord(lat, lon)}`;
}

/** Repli sur le TEXTE de l'adresse, pour les seuls cas sans coordonnées (ticket sans jvid, lecture
 *  DMP indisponible, demande MDU dont la localisation n'est pas lue). Google géocode alors lui-même. */
export function mapUrl(parts: (string | null | undefined)[]): string {
  const q = parts.filter(Boolean).join(' ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}
